/**
 * Utility functions for common error handling patterns
 */
import {
  createGraphQLError,
  GraphQLErrorLike,
  isGraphQLError,
} from "./graphqlErrors";

/**
 * Type definition for Amplify GraphQL error response
 */
interface GraphQLErrorResponse {
  data: null;
  errors: GraphQLErrorLike[];
}

/**
 * Safely converts an unknown error to an Error object with proper type checking.
 * This is useful for handling errors in catch blocks where the error type is unknown.
 *
 * Special handling for Amplify GraphQL error responses that contain an array of errors
 * in the format { data: null, errors: [...] }.
 *
 * @param error - The unknown error caught in a catch block
 * @param context - Optional context message to prepend to the error
 * @returns A proper Error object
 *
 * @example
 * ```typescript
 * try {
 *   await riskyOperation();
 * } catch (error) {
 *   const errorObj = toError(error, "Operation failed");
 *   errorReporter.reportApplicationError(errorObj, {
 *     tags: { component: "MyComponent" }
 *   });
 * }
 * ```
 *
 * @example
 * ```typescript
 * // Handles GraphQL error responses
 * try {
 *   const response = await amplifyClient.graphql({ query, variables });
 *   if (response.errors) {
 *     throw response; // This will be handled by toError()
 *   }
 * } catch (error) {
 *   const errorObj = toError(error, "GraphQL operation failed");
 *   // errorObj.message will contain formatted GraphQL error messages
 * }
 * ```
 */
export function toError(error: unknown, context?: string): Error {
  // If already a GraphQLError (from graphql() wrapper), return as-is
  if (isGraphQLError(error)) {
    if (context && !error.message.startsWith(context)) {
      error.message = `${context}: ${error.message}`;
    }
    return error;
  }

  // Handle raw Amplify GraphQL error responses (legacy code paths)
  if (
    error &&
    typeof error === "object" &&
    "errors" in error &&
    Array.isArray((error as GraphQLErrorResponse).errors) &&
    (error as GraphQLErrorResponse).data === null
  ) {
    const graphQLResponse = error as GraphQLErrorResponse;
    const graphQLError = createGraphQLError(
      graphQLResponse.errors,
      [],
      context,
    );
    // Attach original response for debugging
    graphQLError.originalResponse = graphQLResponse;
    return graphQLError;
  }

  if (error instanceof Error) {
    if (context) {
      // Only wrap if it's a generic Error, not a custom error
      const isCustomError = error.constructor !== Error;
      if (isCustomError) {
        // For custom errors, just update the message if needed, but don't re-wrap
        if (!error.message.startsWith(context)) {
          error.message = `${context}: ${error.message}`;
        }
        return error;
      }
      let message = error.message;
      // If the error message is '[object Object]', it's likely not descriptive.
      // Try to serialize the error object itself for a better message.
      if (message === "[object Object]") {
        try {
          message = JSON.stringify(error);
        } catch (_e) {
          // Fallback if serialization fails
          message = "[Unserializable error object]";
        }
      }
      // Create a new error with the context but preserve the original stack
      const newError = new Error(`${context}: ${message}`);
      // Copy the original stack trace to preserve the call stack
      newError.stack = error.stack;
      // Copy other properties that might be useful
      newError.name = error.name;
      return newError;
    }
    return error;
  }

  let errorMessage: string;
  try {
    // Try to stringify objects for more detailed error messages
    errorMessage =
      typeof error === "object" && error !== null
        ? JSON.stringify(error)
        : String(error);
  } catch (_e) {
    // Fallback for circular references or other stringify errors
    errorMessage =
      "[Unserializable object - likely contains circular references]";
  }

  const finalMessage = context ? `${context}: ${errorMessage}` : errorMessage;
  return new Error(finalMessage);
}

/**
 * Detects a failed dynamic-import / chunk load. This happens when a tab that
 * loaded an older deploy tries to fetch a lazy chunk whose hashed filename no
 * longer exists at origin (Amplify replaces assets on each deploy). The symptom
 * is a blank page / crash; the recovery is to reload so the tab picks up the
 * current bundle. See services/chunkLoadRecovery.ts.
 *
 * Messages differ per browser:
 *  - Chromium: "Failed to fetch dynamically imported module: <url>"
 *  - Firefox:  "error loading dynamically imported module: <url>"
 *  - Safari:   "Importing a module script failed."
 * Vite's preload helper also rejects with "Unable to preload CSS for <url>"
 * when a chunk's stylesheet dependency is the one that 404s.
 */
export function isChunkLoadError(error: unknown): boolean {
  const err = toError(error);
  if (err.name === "ChunkLoadError") return true;
  // "dynamically imported module" covers both Chromium ("Failed to fetch...")
  // and Firefox ("error loading...") phrasings; Safari uses a distinct string.
  return /dynamically imported module|Importing a module script failed|Unable to preload CSS/i.test(
    err.message,
  );
}
